import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ContentPlacement } from '@candu-ai/placement/types';
import { setPreviewSelector, setSnapshot, useCurrentSnapshot } from 'craft/state';
import { usePlacements } from 'craft/state/placements';

import { ClickToPlaceStep, FormSteps } from './components';

enum PlacementStep {
  Place = 'Place',
  Form = 'Form',
}

interface InlinePlacementProps {
  placement: ContentPlacement;
  onEditUrl: VoidFunction;
  placementSaved: {
    values: ContentPlacement;
    id?: string;
  } | null;
  resetPlacementSaved: VoidFunction;
  onGoBack?: VoidFunction;
}

export const InlinePlacement = ({
  placement,
  onEditUrl,
  placementSaved,
  resetPlacementSaved,
  onGoBack,
}: InlinePlacementProps) => {
  const dispatch = useDispatch();
  const { snapshot } = useCurrentSnapshot();
  const { placements } = usePlacements();
  const { values, setFieldValue, resetForm, submitForm } = useFormikContext<ContentPlacement>();
  const [currentStep, setCurrentStep] = useState(
    snapshot && (!placements || placements.length === 0 || !placement.selector)
      ? PlacementStep.Place
      : PlacementStep.Form,
  );

  useEffect(() => {
    // if we change placement we should reset form
    resetForm();
  }, [placement.id]);

  useEffect(() => {
    setCurrentStep(
      snapshot && (!placements || placements.length === 0 || !placement.selector)
        ? PlacementStep.Place
        : PlacementStep.Form,
    );

    if (snapshot && placement.snapshotId !== snapshot.id) {
      // if we have a snapshot, then save/update the id in the placement
      setFieldValue('snapshotId', snapshot.id, false);
    }
  }, [placements, placement, snapshot?.id]);

  useEffect(() => {
    // used to sync placement state between tab and edit placement modal
    if (placementSaved !== null) {
      // when the placement changes are saved, we need to sync the active placement in the snapshot
      dispatch(setSnapshot(snapshot, placementSaved.values));
      // we need to also reset this form with the modal's placement values
      resetForm(placementSaved);
      resetPlacementSaved();
    }
  }, [placementSaved, resetPlacementSaved]);

  if (currentStep === PlacementStep.Place) {
    return (
      <ClickToPlaceStep
        key={placement.id}
        initialValue={values.selector}
        onCancel={() => {
          setCurrentStep(PlacementStep.Form);
          dispatch(setPreviewSelector(values.selector));
        }}
        handleConfirmPlacement={(value: string) => {
          setFieldValue('selector', value, false);
          if (values.id) {
            submitForm().then(() => {
              setCurrentStep(PlacementStep.Form);
              dispatch(setPreviewSelector(undefined));
            });
          } else {
            setCurrentStep(PlacementStep.Form);
            dispatch(setPreviewSelector(value));
          }
        }}
      />
    );
  }

  return (
    <FormSteps
      key={`${placement.id}-${values.selector}`}
      onEditUrl={onEditUrl}
      handleRetargetClick={() => {
        setCurrentStep(PlacementStep.Place);
      }}
      onGoBack={onGoBack}
    />
  );
};
